import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Collapse, Form, Input, Select, Switch, message } from "antd";
import { Ban, Check, ShieldCheck } from "lucide-react";
import axios from "axios";

import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../../design-system/tokens";
import { card, FieldLabel, Note, PanelHead } from "../ssh-proxy/sshProxyUi";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_PAM_API } from "../../../constants";
import { createADPolicy, updateADPolicy, getAllADDomains, listADGroups, listADUsers, listOUs, listADPolicies, policyAction } from "../../../service/adPolicyService";
import { domainFqdn, domainLabel } from "../ad-policies/adPolicyModel";
import {
  RADIUS_ACTIONS,
  DEFAULT_RADIUS_ACTION,
  RADIUS_FORM_SERVICE_TYPES,
  SERVICE_TYPE_HINT,
  buildRadiusPolicyJson,
  invalidTokens,
  isValidClient,
  isValidCallingStation,
  CLIENT_HINT,
  CALLING_STATION_NOTE,
  radiusPolicyFormErrors,
  serviceTypeLabel,
} from "./radiusPolicyModel";

// The form is four sections (handoff 2026-10-22): Name; Who; Which devices; Action.
// Validation runs on Create and lists its messages above the button. mfaMethod and
// replyAttributes (authnull-service #132) are deliberately not offered: the backend treats a
// missing method as push, and push is the only factor in this release.

const ACTION_ICONS = { mfa_required: ShieldCheck, allow: Check, block: Ban };
const ACTION_ACCENT = { mfa_required: AN_COLORS.primary, allow: AN_COLORS.success, block: AN_COLORS.danger };

// Three selectable cards, used as the Action form control (value / onChange from Form.Item).
function ActionCards({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="Action" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))", gap: "0.625rem" }}>
      {RADIUS_ACTIONS.map((a) => {
        const selected = value === a.value;
        const Icon = ACTION_ICONS[a.value];
        const accent = ACTION_ACCENT[a.value];
        return (
          <button
            key={a.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange && onChange(a.value)}
            style={{
              textAlign: "left",
              cursor: "pointer",
              padding: "0.75rem 0.875rem",
              borderRadius: 8,
              border: `1px solid ${selected ? accent : AN_COLORS.line}`,
              boxShadow: selected ? `0 0 0 1px ${accent}` : "none",
              background: selected ? anAlpha(accent, 7) : AN_COLORS.surface,
              color: AN_COLORS.ink,
              fontFamily: AN_FONT,
              display: "flex",
              gap: "0.625rem",
              alignItems: "flex-start",
            }}
          >
            <span
              style={{
                width: "1.75rem",
                height: "1.75rem",
                borderRadius: 7,
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: anAlpha(accent, 10),
                color: accent,
              }}
            >
              {Icon ? <Icon size={15} /> : null}
            </span>
            <span>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600 }}>{a.label}</span>
              <span style={{ display: "block", fontSize: 12, color: AN_COLORS.muted, marginTop: 2 }}>{a.hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

const sectionStyle = { ...card, padding: "1.125rem 1.25rem 0.25rem", marginBottom: "1rem" };

// vpn-1234567-12345678 — same generated-name shape the other policy forms use.
function generatePolicyName() {
  const section = (len) =>
    Array.from({ length: len }, () => Math.floor(Math.random() * 10)).join("");
  return `vpn-${section(7)}-${section(8)}`;
}

function readDraft(isEditMode) {
  const raw = isEditMode
    ? localStorage.getItem("editPolicyData")
    : localStorage.getItem("policyDraft.basicPolicyInformation");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function CreateRadiusPolicyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [messageApi, messageContext] = message.useMessage();

  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get("edit") === "true";

  const [submitting, setSubmitting] = useState(false);
  const [policyId, setPolicyId] = useState(null);

  const [domains, setDomains] = useState([]);
  const [adDomainId, setAdDomainId] = useState(null);

  const [groupOptions, setGroupOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [ouOptions, setOuOptions] = useState([]);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [matchAll, setMatchAll] = useState(false);
  // Calling stations and service types are collapsed under "Advanced": most policies never
  // need them, and a wrong service type silently stops a policy from applying.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Every policy's name, for the "already exists" check; and the messages shown above Create.
  const [existingPolicies, setExistingPolicies] = useState([]);
  const [formErrors, setFormErrors] = useState([]);
  const [attempted, setAttempted] = useState(false);
  // Service types a saved policy carries beyond the four the form offers.
  const [savedServiceTypes, setSavedServiceTypes] = useState([]);

  // The policy being edited: which directory it was saved against, and whether it was
  // paused. Refs, because the directory list arrives asynchronously after the prefill.
  const savedDomainRef = useRef("");
  const wasSuspendedRef = useRef(false);
  const originalNameRef = useRef("");

  const selectedDomain = useMemo(
    () => (domains || []).find((d) => d.id === adDomainId) || null,
    [domains, adDomainId],
  );
  const adDomainFqdn = domainFqdn(selectedDomain);

  // Prefill (Grant Access draft or the policy being edited).
  useEffect(() => {
    const statePolicy = location.state?.policy;
    const draft = statePolicy || readDraft(isEditMode);
    const r = draft?.policyJson?.radius || {};
    if (isEditMode) setPolicyId(draft?.id || draft?.policyId || null);
    if (r.matchAll) setMatchAll(true);
    if (isEditMode) {
      savedDomainRef.current = String(r.domain || "").toLowerCase();
      wasSuspendedRef.current = draft?.status === "Suspended";
      originalNameRef.current = String(draft?.policyName || draft?.policyJson?.policyName || "").trim();
    }
    if ((r.callingStations || []).length || (r.serviceTypes || []).length) setAdvancedOpen(true);
    setSavedServiceTypes(r.serviceTypes || []);
    // "Add policy for this device" on the Network Devices page opens this form with the
    // device already selected.
    const prefillClients = !isEditMode ? location.state?.prefillClients : null;
    form.setFieldsValue({
      policyName: draft?.policyName || draft?.policyJson?.policyName || generatePolicyName(),
      matchAll: !!r.matchAll,
      groups: r.groups || [],
      users: r.users || [],
      ous: r.ous || [],
      // clients: r.clients || [],
      clients: r.clients || prefillClients || [],
      callingStations: r.callingStations || [],
      serviceTypes: r.serviceTypes || [],
      policyFlow: draft?.policyJson?.policyFlow || DEFAULT_RADIUS_ACTION,
    });
  }, [form, isEditMode, location.state]);

  // AD domains → identity is still AD, because FreeRADIUS authenticates against
  // the customer's directory before Authnull is consulted.
  useEffect(() => {
    getAllADDomains({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID() },
      onSuccess: (res) => {
        const list = res?.domains || res?.data || [];
        setDomains(list);
        // setAdDomainId((prev) => prev ?? list?.[0]?.id ?? null);
        // Replaced: an edited policy reopened on the FIRST directory, so saving it silently
        // moved the policy to that directory's domain. Now the saved directory is selected.
        // ponytail: matched by domain name -- with several directories on one domain the
        // first of them is chosen; exact needs the directory id stored on RADIUS policies.
        const saved = savedDomainRef.current;
        const savedMatch = saved ? list.find((d) => String(domainFqdn(d) || "").toLowerCase() === saved) : null;
        setAdDomainId((prev) => prev ?? savedMatch?.id ?? list?.[0]?.id ?? null);
      },
      onFailure: () => messageApi.error("Couldn't load directories"),
    });
  }, [messageApi]);

  // Groups + users + OUs for the picked domain. Loaded top-N and filtered
  // client-side by the Select — the identity resolves through the same AD
  // transitive closure the AD engine uses.
  useEffect(() => {
    if (!adDomainId) {
      setGroupOptions([]);
      setUserOptions([]);
      setOuOptions([]);
      return;
    }
    listADGroups({
      payload: { org_Id: ORG_ID(), tenant_Id: DOMAIN_ID(), adId: adDomainId, filter: "", page: 1, pageSize: 200 },
      onSuccess: (res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setGroupOptions(
          list.map((g) => ({
            value: g.groupName,
            label: g.memberCount != null ? `${g.groupName} · ${g.memberCount} ${g.memberCount === 1 ? "user" : "users"}` : g.groupName,
          })),
        );
      },
      onFailure: (e) => messageApi.error(e?.message || "Couldn't load directory groups"),
    });
    listADUsers({
      payload: { org_Id: ORG_ID(), tenant_Id: DOMAIN_ID(), adId: adDomainId, filter: "", page: 1, pageSize: 200 },
      onSuccess: (res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setUserOptions(list.map((u) => ({ value: u.username, label: u.email ? `${u.username} · ${u.email}` : u.username })));
      },
      onFailure: (e) => messageApi.error(e?.message || "Couldn't load directory users"),
    });
    listOUs({
      payload: { org_Id: ORG_ID(), tenant_Id: DOMAIN_ID(), adId: adDomainId, domain: adDomainFqdn },
      onSuccess: (res) => {
        const list = res?.data || res?.ous || [];
        const scoped = (Array.isArray(list) ? list : []).filter((ou) => {
          if (typeof ou === "string") return true;
          const ouAdId = ou?.ad_id ?? ou?.adId;
          if (ouAdId != null) return Number(ouAdId) === Number(adDomainId);
          return true;
        });
        setOuOptions(
          scoped
            .map((ou) => {
              if (typeof ou === "string") return { value: ou, label: ou };
              const value = ou?.ou_name || ou?.ouName || ou?.name || ou?.dn;
              return value ? { value, label: value } : null;
            })
            .filter(Boolean),
        );
      },
      onFailure: () => {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adDomainId, adDomainFqdn]);

  // Registered network devices → the clients picker. The RADIUS server registers
  // devices as it sees them (network_device/CreateDevice); the admin can also type
  // a NAS-Identifier, IP, CIDR or * that has not been seen yet.
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await axios.post(
          `${REACT_APP_PAM_API}/network_device/ListDevices`,
          { orgId: ORG_ID(), tenantId: DOMAIN_ID(), pageId: 1, pageSize: 200, search: "" },
          { headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } },
        );
        const devices = res?.data?.network_devices || [];
        const seen = new Set();
        const opts = [];
        devices.forEach((d) => {
          const value = d.nas_identifier || d.ip_address;
          if (!value || seen.has(value)) return;
          seen.add(value);
          const name = d.name || d.device_type;
          opts.push({ value, label: name && name !== value ? `${value} · ${name}` : value });
        });
        setDeviceOptions(opts);
      } catch {
        // The device registry is optional context; the field still accepts typed
        // entries, so a failed load is not fatal to authoring a policy.
        setDeviceOptions([]);
      }
    };
    fetchDevices();
  }, []);

  // The policy list, for the name-uniqueness check (same query as the Policies page).
  useEffect(() => {
    listADPolicies({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyType: "radius", page: 1, limit: 200 },
      onSuccess: (res) => setExistingPolicies(res?.policies || res?.data || []),
      onFailure: () => setExistingPolicies([]),
    });
  }, []);

  // Names a new / renamed policy must not take. In edit mode the policy itself is left out,
  // by id and by its saved name (an update saves a new version under a new id).
  const existingNames = useMemo(
    () =>
      (existingPolicies || [])
        .filter((p) => {
          if (!isEditMode) return true;
          const id = p?.id || p?.policyId;
          if (policyId && id === policyId) return false;
          return String(p?.policyName || "").trim().toLowerCase() !== originalNameRef.current.toLowerCase();
        })
        .map((p) => p?.policyName),
    [existingPolicies, isEditMode, policyId],
  );

  const serviceTypeOptions = useMemo(() => {
    const opts = RADIUS_FORM_SERVICE_TYPES.map((t) => ({ value: t.value, label: t.label }));
    savedServiceTypes.forEach((v) => {
      if (!opts.some((o) => o.value === v)) opts.push({ value: v, label: serviceTypeLabel(v) });
    });
    return opts;
  }, [savedServiceTypes]);

  const collectErrors = (values) => {
    const errors = radiusPolicyFormErrors({
      policyName: values.policyName,
      existingNames,
      matchAll: !!values.matchAll,
      groups: values.groups,
      users: values.users,
      ous: values.ous,
    });
    const badClients = invalidTokens(values.clients, isValidClient);
    if (badClients.length) errors.push(`Not a valid device entry: ${badClients.join(", ")}.`);
    const badStations = invalidTokens(values.callingStations, isValidCallingStation);
    if (badStations.length) errors.push(`Not a valid calling station: ${badStations.join(", ")}.`);
    return errors;
  };

  // After the first Create attempt the messages follow the form as it is fixed.
  const onValuesChange = (_, all) => {
    if (attempted) setFormErrors(collectErrors(all));
  };

  const clearDrafts = () => {
    localStorage.removeItem("policyDraft.basicPolicyInformation");
    localStorage.removeItem("editPolicyData");
    localStorage.removeItem("formSessionData");
  };

  const handleFinish = (values) => {
    setAttempted(true);
    const errors = collectErrors(values);
    setFormErrors(errors);
    if (errors.length) return;

    const isMatchAll = !!values.matchAll;
    const policyName = values.policyName?.trim();
    const policyJson = buildRadiusPolicyJson({
      policyName,
      policyFlow: values.policyFlow,
      domain: adDomainFqdn,
      users: isMatchAll ? [] : values.users,
      groups: isMatchAll ? [] : values.groups,
      ous: isMatchAll ? [] : values.ous,
      matchAll: isMatchAll,
      clients: values.clients,
      callingStations: values.callingStations,
      serviceTypes: values.serviceTypes,
    });

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      policyName,
      policyType: "radius",
      generatedBy: "Manual",
      policyJson,
    };

    setSubmitting(true);
    const finish = () => {
      messageApi.success(isEditMode ? "RADIUS policy updated" : "RADIUS policy created");
      clearDrafts();
      setSubmitting(false);
      navigate("/pam/policy/v2");
    };
    const onSuccess = (response) => {
      const newPolicyId = response?.updatedPolicyID?.data;
      if (newPolicyId) setPolicyId(newPolicyId);
      // The shared update API saves every edit as a new, ACTIVE version -- so editing a
      // paused policy silently switched it back on. That API is shared with AD and database
      // policies and is not ours to change, so the paused state is restored here instead.
      // ponytail: the new version is active for the moment between the two calls.
      if (isEditMode && wasSuspendedRef.current && newPolicyId) {
        policyAction({
          payload: {
            orgId: ORG_ID(),
            tenantId: DOMAIN_ID(),
            action: { actionType: "status", actionIds: [newPolicyId], actionValue: "Suspended" },
          },
          onSuccess: finish,
          onFailure: () => {
            messageApi.warning("Policy updated, but it could not be kept paused. Pause it again from the list.");
            finish();
          },
        });
        return;
      }
      finish();
    };
    const onFailure = () => {
      messageApi.error(isEditMode ? "Failed to update policy" : "Failed to create policy");
      setSubmitting(false);
    };

    if (isEditMode && policyId) {
      updateADPolicy({ payload: { ...payload, id: policyId, updatedBy: "admin_user" }, onSuccess, onFailure });
    } else {
      createADPolicy({ payload, onSuccess, onFailure });
    }
  };

  const handleCancel = () => {
    clearDrafts();
    navigate("/pam/policy/v2");
  };

  const directoryOptions = domains.map((d) => {
    const name = domainLabel(d);
    const fqdn = domainFqdn(d);
    return { value: d.id, label: name && fqdn && name !== fqdn ? `${name} · ${fqdn}` : name || fqdn };
  });
  const identityDisabled = matchAll || !adDomainId;

  return (
    <div
      style={{
        minHeight: "calc(100vh - 64px)",
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: "clamp(1rem, 3vw, 1.75rem) clamp(1rem, 3vw, 2rem)",
      }}
    >
      {messageContext}
      <div style={{ maxWidth: "47.5rem", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: AN_COLORS.muted, marginBottom: 8 }}>
            <Link to="/pam/policy/v2" style={{ color: AN_COLORS.muted, textDecoration: "none" }}>
              Policies
            </Link>
            <span style={{ color: AN_COLORS.line }}>/</span>
            <span style={{ color: AN_COLORS.ink, fontWeight: 500 }}>{isEditMode ? "Edit" : "New RADIUS"}</span>
          </div>
          <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
            Admin · Policies · RADIUS
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4, color: AN_COLORS.ink }}>
            {isEditMode ? "Edit RADIUS policy" : "Create RADIUS policy"}
          </h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            Require an Authnull push when a directory user reaches a VPN, switch or firewall over RADIUS. FreeRADIUS verifies the
            password first; this policy is the second factor.
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onValuesChange={onValuesChange}
          initialValues={{ policyFlow: DEFAULT_RADIUS_ACTION }}
          requiredMark={false}
        >
          {/* 1. Name */}
          <section style={sectionStyle}>
            <PanelHead eyebrow="1 · Name" title="Name" />
            <Form.Item label={<FieldLabel required>Policy name</FieldLabel>} name="policyName">
              <Input placeholder="vpn-mfa-policy" />
            </Form.Item>
          </section>

          {/* 2. Who */}
          <section style={sectionStyle}>
            <PanelHead eyebrow="2 · Who" title="Who does this apply to?" />
            <Form.Item label={<FieldLabel required>Directory</FieldLabel>}>
              <Select
                value={adDomainId}
                onChange={setAdDomainId}
                placeholder="Select a directory"
                showSearch
                optionFilterProp="label"
                options={directoryOptions}
              />
            </Form.Item>

            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1.25rem" }}>
              <Form.Item name="matchAll" valuePropName="checked" noStyle>
                <Switch id="radius-match-all" onChange={setMatchAll} />
              </Form.Item>
              <label htmlFor="radius-match-all" style={{ cursor: "pointer" }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: AN_COLORS.ink }}>Everyone in the directory</span>
                <span style={{ display: "block", fontSize: 12, color: AN_COLORS.muted, marginTop: 2 }}>
                  Every user in this directory who signs in over RADIUS. Turn off to pick groups, users or OUs.
                </span>
              </label>
            </div>

            {/* Chip inputs: type to search the synced directory, Enter adds, × or Backspace removes. */}
            <Form.Item label={<FieldLabel>Groups</FieldLabel>} name="groups">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder={matchAll ? "Everyone in the directory" : "e.g. VPN-Users"} options={groupOptions} disabled={identityDisabled} />
            </Form.Item>
            <Form.Item label={<FieldLabel>Users</FieldLabel>} name="users">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder={matchAll ? "Everyone in the directory" : "Individual users"} options={userOptions} disabled={identityDisabled} />
            </Form.Item>
            <Form.Item label={<FieldLabel>OUs</FieldLabel>} name="ous">
              <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder={matchAll ? "Everyone in the directory" : "Organizational units"} options={ouOptions} disabled={identityDisabled} />
            </Form.Item>
          </section>

          {/* 3. Which devices */}
          <section style={sectionStyle}>
            <PanelHead eyebrow="3 · Which devices" title="Which devices?" />
            <Form.Item
              label={<FieldLabel hint="Empty means all devices">Devices</FieldLabel>}
              name="clients"
              extra={CLIENT_HINT}
            >
              <Select mode="tags" allowClear placeholder="All devices" options={deviceOptions} tokenSeparators={[",", " "]} optionFilterProp="label" />
            </Form.Item>

            {/* forceRender keeps both fields mounted while collapsed, so their values are still saved. */}
            <Collapse
              ghost
              style={{ marginBottom: "0.75rem" }}
              activeKey={advancedOpen ? ["advanced"] : []}
              onChange={(keys) => setAdvancedOpen((Array.isArray(keys) ? keys : [keys]).includes("advanced"))}
              items={[
                {
                  key: "advanced",
                  label: <span style={{ fontSize: 13, fontWeight: 500, color: AN_COLORS.ink2 }}>Advanced</span>,
                  forceRender: true,
                  children: (
                    <>
                      <Form.Item label={<FieldLabel hint="Empty means any station">Calling stations</FieldLabel>} name="callingStations" extra={CALLING_STATION_NOTE}>
                        <Select mode="tags" allowClear placeholder="Any station" tokenSeparators={[",", " "]} />
                      </Form.Item>
                      <Form.Item label={<FieldLabel>Service type</FieldLabel>} name="serviceTypes" extra={SERVICE_TYPE_HINT}>
                        <Select mode="multiple" allowClear placeholder="Any" options={serviceTypeOptions} />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />
          </section>

          {/* 4. Action */}
          <section style={{ ...sectionStyle, paddingBottom: "1.125rem" }}>
            <PanelHead eyebrow="4 · Action" title="What happens on a match?" />
            <Form.Item name="policyFlow" style={{ marginBottom: "0.875rem" }}>
              <ActionCards />
            </Form.Item>
            <Note tone="info">
              No matching policy means allow. A login no policy covers goes through, so installing the bridge never cuts off a
              working VPN.
            </Note>
          </section>

          {formErrors.length > 0 && (
            <div role="alert" style={{ marginBottom: "0.75rem" }}>
              <Note tone="danger">
                {formErrors.length === 1 ? (
                  formErrors[0]
                ) : (
                  <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                    {formErrors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                )}
              </Note>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: "0.625rem", marginTop: "0.5rem" }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {isEditMode ? "Save changes" : "Create policy"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
